import { Component, OnDestroy, OnInit, OnChanges, ViewChild, ChangeDetectorRef, ElementRef, Input, Pipe, PipeTransform, Output, EventEmitter, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { UntypedFormBuilder, Validators, ControlContainer, FormGroupDirective, FormControl, UntypedFormGroup } from '@angular/forms';
import { Subject, Observable, of } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { DatePipe , Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { VendorService } from '../../services/vendor.service';
import { PaymentsService } from '../../services/payments.service';
import { GlobalsService } from '../../services/globals.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { MatDialog } from '@angular/material/dialog';
import { GlPreviewModalComponent, GlPreviewModalData } from '../../shared/gl-preview-modal/gl-preview-modal.component';
import {
	VendorRemitPreviewModalComponent,
	VendorRemitPreviewModalData,
	VendorRemitPreviewTabRequest
} from '../../shared/vendor-remit-preview-modal/vendor-remit-preview-modal.component';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { resolveDefaultBankAccountCode } from '../../utils/bank-account-default';
import { ApRemittanceImportComponent } from '../ap-remittance-import/ap-remittance-import.component';

@Component({
	selector: 'app-vendor-payment-entry',
	templateUrl: './vendor-payment-entry.component.html',
	styleUrls: ['./vendor-payment-entry.component.scss']
})

//TODO - ADD TO PAYMENT CART
export class VendorPaymentEntryComponent implements OnInit, OnChanges {
	@Input() vendor;
	@Input() bankRecContext: any = null;
	@Output() newvendordata = new EventEmitter<boolean>();
	@ViewChild('apRemit') apRemitImport: ApRemittanceImportComponent;
	apRemitOpen = false;   // mirrors the importer's open state; hides the Allocate section while dropping

	private vendorSubscription: any;
	bankaccounts: any = [];
	payment_methods: any = [];
	currencies: any[] = [];
	paymentForm: UntypedFormGroup;
	payments: any = [];
	total_payments = 0.00;
	today = new Date();
	chargetype: any = 'Check';
	result: any = false;

	vendorInvoices: any[] = [];
	vendorCredits: any[] = [];
	vendorPayments: any[] = [];
	// Virtual-scroll feeds. Both panes render only the rows in view: THUB (Cleve)
	// has ~2,200 open invoices and ~1,900 open credits, and a mat-form-field per
	// row across 4,000 rows hung the tab. Row extent must match .alloc-row in the
	// scss or the viewport mis-positions.
	rowHeight = 74;
	availableFrom: any[] = [];
	filteredFrom: any[] = [];
	filteredInvoices: any[] = [];
	fromFilter = '';
	invoiceFilter = '';
	trackByTransId = (_: number, row: any) => (row && row.id != null) ? row.id : row;
	allocations_from: any[] = [];
	allocations_to: any[] = [];
	// Credit-note -> invoice pairs planned by the AP remittance importer (netted
	// drafts). They ride along in the post payload; add() re-caps them against live
	// balances and writes them in the same transaction as the payment.
	credit_allocations: any[] = [];
	allocation_from_total = 0.00;
	allocation_to_total = 0.00;
	user: any = null;

	constructor(private route: ActivatedRoute, private fb: UntypedFormBuilder, private vendorService: VendorService, public router: Router, private location: Location, private globalsService: GlobalsService, private globalSearchService: GlobalSearchService, private paymentsService: PaymentsService, private dialog: MatDialog) {}


	/** Resolve exchange rate for a currency code from the loaded currencies list.
	 *  Falls back to 1 if rate is missing or 0 (0 is never a valid exchange rate). */
	private rateForCurrcode(code: string): number {
		const cur = (this.currencies || []).find((c: any) => c.currabrev === code);
		if (cur) {
			const r = parseFloat(cur.rate);
			if (!isNaN(r) && r > 0) { return r; }
		}
		// Fall back to vendor exrate if currencies list doesn't have a usable rate
		const vendorRate = parseFloat(this.vendor?.exrate);
		return (!isNaN(vendorRate) && vendorRate > 0) ? vendorRate : 1;
	}

	/** Return the base currency code (rate == 1) from the loaded currencies list. */
	private getBaseCurrcode(): string {
		const base = (this.currencies || []).find((c: any) => {
			const r = parseFloat(c.rate);
			return !isNaN(r) && Math.abs(r - 1) < 0.0001;
		});
		return base?.currabrev || '';
	}

	/** Push vendor currency + exchange rate into the form once we know both vendor and currencies.
	 *  Falls back to base currency (rate = 1) when vendor has no currcode set. */
	private syncCurrencyToForm(): void {
		if (!this.paymentForm || !this.currencies?.length) { return; }
		const code = this.vendor?.currcode || this.getBaseCurrcode();
		if (!code) { return; }
		this.paymentForm.get('currcode')?.setValue(code, { emitEvent: false });
		this.paymentForm.get('exrate')?.setValue(this.rateForCurrcode(code), { emitEvent: false });
	}

	ngOnInit(): void {
		//vendor is required. Not on GL
		this.paymentForm = this.fb.group({
			payment_type: ['', Validators.required],
			bankaccount: ['', Validators.required],
			datepaid: [this.today, Validators.required],
			dueDate: [null],
			checkno: [''],
			reference: [''],
			description: ['', Validators.required],
			amount: [0.00, Validators.required],
			discount: [0, Validators.required],
			currcode: [''],
			exrate: [1],
		});
		this.applyBankRecContext();

		// Auto-update due date when the payment date changes (computed by backend).
		// Driven off datepaid — the single date on this screen, and the one that sets
		// the G/L posting date.
		this.paymentForm.get('datepaid')?.valueChanges.pipe(
			debounceTime(200),
			switchMap((d: Date) => {
				if (!d || !this.vendor) { return of(null); }
				return this.vendorService.computeDueDate({
					invoiceDate: d instanceof Date ? d.toISOString() : String(d),
					daysbeforedue: parseInt(this.vendor?.daysbeforedue) || 0,
					dayinfollowingmonth: parseInt(this.vendor?.dayinfollowingmonth) || 0,
					statementperiods: parseInt(this.vendor?.statementperiods) || 0,
				});
			})
		).subscribe((result: any) => {
			if (result?.duedate) {
				this.paymentForm.get('dueDate')?.setValue(new Date(result.duedate), { emitEvent: false });
			}
		});

		// When user manually changes currency, auto-fill the exchange rate
		this.paymentForm.get('currcode').valueChanges.subscribe((code: string) => {
			if (!code) { return; }
			this.paymentForm.get('exrate').setValue(this.rateForCurrcode(code), { emitEvent: false });
		});

		combineLatest([
			this.globalSearchService.bankaccounts,
			this.globalSearchService.company
		]).pipe(
			filter(([accounts]) => Array.isArray(accounts) && accounts.length > 0)
		).subscribe(([accounts, company]) => {
			this.bankaccounts = accounts;
			this.paymentForm.get('bankaccount').setValue(
				this.bankRecContext?.erp_bankact || resolveDefaultBankAccountCode(accounts, company?.defaultBankAccount)
			);
			this.loadNextCheckNumber();
		});

		// A different account is a different check book, and switching to Check needs a
		// number. Both re-pull bankaccounts.nextchecknum so the clerk never hand-picks
		// one the sequence has already handed out.
		this.paymentForm.get('bankaccount').valueChanges.subscribe(() => this.loadNextCheckNumber());
		this.paymentForm.get('payment_type').valueChanges.subscribe(() => this.loadNextCheckNumber());

		this.globalSearchService.payment_methods.subscribe((payment_methods: any) => {
			this.payment_methods = payment_methods;
			if (payment_methods) {
				this.paymentForm.get('payment_type').setValue(payment_methods[0].paymentname);
			}
		});

		this.globalSearchService.user.subscribe((result: any) => {
			this.user = result;
		});

		// Currencies must be loaded before the mat-select can display a value.
		// syncCurrencyToForm() sets both currcode and exrate after options exist.
		this.globalSearchService.currencies.subscribe((currencies: any) => {
			this.currencies = currencies || [];
			this.syncCurrencyToForm();
		});

		if (this.vendor && this.vendor.supplierid) {
			this.loadVendorTransactions();
			if (this.vendor.duedate) {
				this.paymentForm.get('dueDate')?.setValue(new Date(this.vendor.duedate), { emitEvent: false });
			}
		}
	}

	ngOnChanges() {
		if (this.vendor && this.vendor.supplierid) {
			this.loadVendorTransactions();
			this.syncCurrencyToForm();
			if (this.paymentForm && this.vendor.duedate) {
				this.paymentForm.get('dueDate')?.setValue(new Date(this.vendor.duedate), { emitEvent: false });
			}
		}
		this.applyBankRecContext();
	}

	private applyBankRecContext(): void {
		if (!this.paymentForm || !this.bankRecContext) { return; }
		const d = new Date(this.bankRecContext.postedYmd || this.bankRecContext.posted);
		this.paymentForm.patchValue({
			bankaccount: this.bankRecContext.erp_bankact || this.paymentForm.get('bankaccount')?.value,
			datepaid: isNaN(d.getTime()) ? this.today : d,
			reference: 'BANK-' + String(this.bankRecContext.sf_id || '').slice(-12),
			description: this.bankRecContext.description || '',
			amount: Math.abs(Number(this.bankRecContext.amount) || 0),
		}, { emitEvent: false });
	}

	private isCheckPayment(): boolean {
		return String(this.paymentForm?.get('payment_type')?.value || '').trim().toLowerCase() === 'check';
	}

	/**
	 * Pull bankaccounts.nextchecknum for the selected account into Check #. The screen
	 * used to leave this blank, so the number came out of the clerk's head and the
	 * account sequence never moved — CORR check 1289 (2026-08-19) was issued here and
	 * then offered again by the next AP check run. Same lookup the check-printing screen
	 * uses. Left alone when the account reports no sequence (0/blank), rather than
	 * silently starting at 1.
	 */
	private loadNextCheckNumber(): void {
		if (!this.paymentForm || !this.isCheckPayment()) { return; }
		const accountcode = String(this.paymentForm.get('bankaccount')?.value || '').trim();
		if (!accountcode) { return; }
		this.paymentsService.getCheckSetup({ bankactdef: accountcode }).subscribe((r: any) => {
			const next = r && r.CheckNum != null ? String(r.CheckNum).trim() : '';
			if (next === '' || next === '0') { return; }
			this.paymentForm.get('checkno')?.setValue(this.bumpForPendingChecks(next), { emitEvent: false });
		});
	}

	/**
	 * The server advances the sequence one payment at a time, but a batch is posted in
	 * one call — so N checks staged here would all prefill the same number. Step past
	 * the numeric check numbers already staged on this account.
	 */
	private bumpForPendingChecks(next: string): string {
		if (!/^\d+$/.test(next)) { return next; }
		const accountcode = String(this.paymentForm.get('bankaccount')?.value || '').trim();
		const staged = (this.payments || [])
			.filter((p: any) => String(p?.payment_type || '').trim().toLowerCase() === 'check'
				&& String(p?.bankaccount || '').trim() === accountcode
				&& /^\d+$/.test(String(p?.checkno || '').trim()))
			.map((p: any) => parseInt(String(p.checkno).trim(), 10));
		if (!staged.length) { return next; }
		return String(Math.max(parseInt(next, 10), Math.max(...staged) + 1));
	}

	addPayment() {
		if (this.paymentForm.valid) {

			this.payments.push(this.paymentForm.value);
			this.updateTotals();

			this.globalSearchService.showNotification('Payment added to batch', 'success', 'bottom', 'right');
			this.paymentForm.get('amount').setValue(0.00);
			this.paymentForm.get('checkno').setValue('');
			this.paymentForm.get('discount').setValue(0);
			this.loadNextCheckNumber();
		} else {
			this.globalSearchService.getFormValidationErrors(this.paymentForm);
		}
	}

	setChargeType(event: any) {
		this.chargetype = event.value
		this.updatePaymentType(event.value)
	}

	updatePaymentType(method : any) {
		this.paymentForm.get('payment_type').setValue(method);
	}

	openGlPreview(): void {
		if (!this.payments.length) {
			this.globalSearchService.showNotification('Add at least one payment to the batch to preview GL.', 'info', 'bottom', 'right');
			return;
		}
		if (!this.vendor || !this.vendor.supplierid) {
			this.globalSearchService.showNotification('Vendor is required.', 'warning', 'bottom', 'right');
			return;
		}
		const payments = this.payments.map((p: any) => ({
			...p,
			amount: this.getPaymentTotal(p),
			discount: 0,
		}));
		this.dialog.open(GlPreviewModalComponent, {
			width: '1160px',
			maxWidth: '98vw',
			panelClass: 'gl-preview-dialog-panel',
			data: {
				title: 'Vendor payment — GL preview',
				getPreview: (_excludeTypenos?: number[], _sampleScope?: 'all' | 'current_year') =>
					this.paymentsService.previewVendorPaymentGl({ vendor: this.vendor, payments }),
			} as GlPreviewModalData,
		});
	}

	openRemittancePreview(): void {
		if (!this.payments.length) {
			this.globalSearchService.showNotification('Add at least one payment to the batch first.', 'info', 'bottom', 'right');
			return;
		}
		if (!this.vendor || !this.vendor.supplierid) {
			this.globalSearchService.showNotification('Vendor is required.', 'warning', 'bottom', 'right');
			return;
		}
		const allocations = this.buildRemitAllocationsForVendor();
		if (!allocations.length) {
			this.globalSearchService.showNotification('Select invoices and enter allocations before previewing the remittance email.', 'info', 'bottom', 'right');
			return;
		}
		const currency = this.vendor.currcode || 'USD';
		const tabs: VendorRemitPreviewTabRequest[] = this.payments.map((pay: any, i: number) => ({
			label: `Payment ${i + 1} (${pay.payment_type || 'Payment'})`,
			supplier_id: this.vendor.supplierid,
			context: {
				payment_total: this.getPaymentTotal(pay),
				payment_discount: parseFloat(pay.discount) || 0,
				payment_reference: pay.reference || '',
				checkno: pay.checkno || '',
				payment_type: pay.payment_type || '',
				post_date: this.formatRemitPostDate(pay.datepaid),
				bank_account: pay.bankaccount || '',
				currency
			},
			allocations
		}));
		this.dialog.open(VendorRemitPreviewModalComponent, {
			width: '760px',
			maxWidth: '98vw',
			data: {
				title: 'Remittance email preview (same as sent after Post Payments)',
				tabs
			} as VendorRemitPreviewModalData
		});
	}

	private buildRemitAllocationsForVendor(): Array<{ trans_id: number; amount: number }> {
		const out: Array<{ trans_id: number; amount: number }> = [];
		const vid = this.vendor?.supplierid;
		if (!vid) return out;
		for (const inv of this.allocations_to || []) {
			const sid = inv.supplierid != null ? inv.supplierid : inv.supplierno;
			if (String(sid) !== String(vid)) continue;
			const allocationAmount = parseFloat(inv.allocation) || 0;
			const discountAmount = parseFloat(inv.discount) || 0;
			const total = Math.round((allocationAmount + discountAmount) * 100) / 100;
			if (total > 0 && inv.id != null) {
				out.push({ trans_id: +inv.id, amount: total });
			}
		}
		return out;
	}

	private formatRemitPostDate(d: any): string {
		if (d instanceof Date) {
			return d.toISOString().slice(0, 10);
		}
		if (typeof d === 'string' && d.length >= 10) {
			return d.slice(0, 10);
		}
		return new Date().toISOString().slice(0, 10);
	}

	postPayments() {
		if (this.payments.length) {
			const allocation_data = { to: this.allocations_to };
			const data = {
				payments: this.payments, vendor: this.vendor, to: this.allocations_to,
				credit_allocations: this.credit_allocations || [],
			}
			// Captured before the batch resets — lets the remittance importer archive
			// its batch against the payment that consumed it.
			const paymentRef = this.payments[0]?.reference || '';
			this.paymentsService.addVendorPayment(data).subscribe((result: any) => {
				this.result = result
				if (!result?.success) {
					this.globalSearchService.showNotification(result?.message || 'Payment could not be posted', 'danger', 'bottom', 'right');
					return;
				}
				this.globalSearchService.showNotification('Payment added', 'success', 'bottom', 'right');
				for (const w of (result.credit_warnings || [])) {
					this.globalSearchService.showNotification(w, 'warning', 'bottom', 'right');
				}
				this.credit_allocations = [];
				this.apRemitImport?.onPaymentPosted(paymentRef);
				this.payments = [];
				this.paymentForm.get('description').setValue('');
				this.paymentForm.get('amount').setValue(0.00);
				this.paymentForm.get('discount').setValue(0);
				this.paymentForm.get('reference').setValue('');
				this.paymentForm.get('checkno').setValue('');
				this.paymentForm.markAsUntouched();
				// Sequence just moved on the server - re-read it for the next check.
				this.loadNextCheckNumber();
				this.allocations_from = [];
				this.allocations_to = [];
				this.allocation_from_total = 0;
				this.allocation_to_total = 0;
				this.loadVendorTransactions();
				this.newvendordata.emit(this.vendor.supplierid);
			});
		}
	}

	removePayment(payment: any) {
		const index = this.payments.indexOf(payment);
		this.payments.splice(index, 1);
		this.updateTotals();
	}

	updatePaymentAmount(value: any, payment: any) {
		const index = this.payments.indexOf(payment);
		this.payments[index].amount = value;
	}
	getPaymentTotal(pay: any) {
		const discount = parseFloat(pay.amount) * (parseFloat(pay.discount) / 100);
		return (parseFloat(pay.amount) - discount);
	}

	updateTotals() {
		this.total_payments = this.payments.reduce(function(accumulator: number, payments: any) {
			const discount = parseFloat(payments.amount) * (parseFloat(payments.discount) / 100);
			return accumulator + (parseFloat(payments.amount) - discount);
		}, 0);
	}

	ngAfterViewInit() {
		//defaults
	}

	loadVendorTransactions() {
		if (this.vendor && this.vendor.supplierid) {
			this.paymentsService.getVendorAccountPayables(this.vendor.supplierid).subscribe((results: any) => {
				this.vendorInvoices = results.invoices || [];
				this.vendorCredits = results.credits || [];
				this.vendorPayments = results.payments || [];

				this.vendorInvoices.forEach((inv: any) => {
					inv.allocation = 0;
					inv.selected = false;
				});

				this.vendorCredits.forEach((cred: any) => {
					cred.allocation = 0;
					cred.selected = false;
				});

				this.vendorPayments.forEach((pay: any) => {
					pay.allocation = 0;
					pay.selected = false;
				});

				// Credits and payments share one pane, so they share one viewport —
				// two nested scrollers inside a fixed-height box is unusable.
				// typename on each row still says which it is.
				this.availableFrom = this.vendorCredits.concat(this.vendorPayments);
				this.applyFromFilter(this.fromFilter);
				this.applyInvoiceFilter(this.invoiceFilter);

				this.autoSelectBankRecInvoice();
			});
		}
	}

	/**
	 * Filtering is recomputed on keystroke, never per change-detection pass — a
	 * method call in the template would re-scan 2,200 rows on every mouse move.
	 * Rows filtered out of view keep their selection and stay in allocations_to;
	 * the header counts are what tell the clerk that.
	 */
	private rowMatches(row: any, needle: string): boolean {
		if (!needle) { return true; }
		const hay = [row.transno, row.suppreference, row.trandate, row.typename, row.leftto]
			.map((v: any) => String(v == null ? '' : v)).join(' ').toLowerCase();
		return hay.indexOf(needle) >= 0;
	}

	applyFromFilter(value: string): void {
		this.fromFilter = value || '';
		const needle = this.fromFilter.trim().toLowerCase();
		this.filteredFrom = needle
			? this.availableFrom.filter((r: any) => this.rowMatches(r, needle))
			: this.availableFrom.slice();
	}

	applyInvoiceFilter(value: string): void {
		this.invoiceFilter = value || '';
		const needle = this.invoiceFilter.trim().toLowerCase();
		this.filteredInvoices = needle
			? this.vendorInvoices.filter((r: any) => this.rowMatches(r, needle))
			: this.vendorInvoices.slice();
	}

	/**
	 * A payment launched from bank rec can safely pre-allocate when exactly one
	 * open supplier invoice equals the withdrawal. Ambiguous repeated amounts
	 * remain for the operator to choose.
	 */
	private autoSelectBankRecInvoice(): void {
		if (!this.bankRecContext) { return; }
		const amount = Math.round(Math.abs(Number(this.bankRecContext.amount) || 0) * 100);
		if (!amount) { return; }
		const exact = this.vendorInvoices.filter((inv: any) => Math.round(Math.abs(Number(inv.leftto) || 0) * 100) === amount);
		if (exact.length !== 1) { return; }
		const invoice = exact[0];
		invoice.selected = true;
		invoice.allocation = Math.abs(Number(invoice.leftto) || 0);
		this.allocations_to = [invoice];
		this.updateAllocationTotals();
	}

	/**
	 * The AP remittance importer emits its included lines — already re-verified
	 * against the live ledger server-side. Tick and fill the invoice allocations
	 * here in one shot: rows present in the loaded list are checked like manual
	 * clicks; rows NOT in the list (the list is empty or partial on vendors with
	 * thousands of open invoices — the exact accounts this feature exists for)
	 * become minimal allocation entries, which is all postPayments()/add()
	 * actually reads (id, supplierno, allocation, discount). Replaces the
	 * current selection (a re-apply after edits must not double up). Money still
	 * posts only through the existing Post Payments button.
	 */
	applyApRemit(ev: any): void {
		const lines: any[] = ev?.lines || [];
		const creditAllocations: any[] = ev?.credit_allocations || [];
		if (ev?.reset) { this.credit_allocations = []; return; }
		if (!lines.length && !creditAllocations.length) { return; }

		// Clear the current selection first — apply is authoritative.
		this.vendorInvoices.forEach((inv: any) => { inv.selected = false; inv.allocation = 0; });
		this.allocations_to = [];
		// The importer already capped these at each document's live open balance;
		// add() caps them again at post time.
		this.credit_allocations = creditAllocations;

		let applied = 0, total = 0;
		for (const l of lines) {
			const alloc = Math.round((parseFloat(l.alloc) || 0) * 100) / 100;
			if (alloc <= 0 || !+l.supptransid) { continue; }
			let inv = this.vendorInvoices.find((i: any) => +i.id === +l.supptransid);
			if (inv) {
				inv.selected = true;
				inv.allocation = Math.min(alloc, Math.abs(parseFloat(inv.leftto) || 0));
			} else {
				inv = {
					id: +l.supptransid,
					supplierno: this.vendor.supplierid,
					suppreference: l.invoice_ref,
					allocation: alloc,
					discount: 0,
					leftto: alloc,
					selected: true,
				};
			}
			this.allocations_to.push(inv);
			applied++;
			total += parseFloat(inv.allocation) || 0;
		}
		this.updateAllocationTotals();
		total = Math.round(total * 100) / 100;

		// Fill the payment amount when the operator hasn't set one (bank rec
		// context already did; never overwrite a nonzero amount).
		const amt = parseFloat(this.paymentForm?.get('amount')?.value) || 0;
		if (!amt && total > 0) { this.paymentForm.get('amount').setValue(total); }

		const creditTotal = Math.round((parseFloat(ev?.credit_applied_total) || 0) * 100) / 100;
		const creditNote = creditTotal > 0
			? (' + ' + creditTotal.toFixed(2) + ' of credit notes queued against them')
			: '';
		this.globalSearchService.showNotification(
			'Applied ' + applied + ' invoices (' + total.toFixed(2) + ') to the payment' + creditNote,
			'success', 'bottom', 'right');
	}

	/**
	 * "Post Payment" clicked on the remittance importer after an apply. Same two
	 * clicks the operator would do below, in order: stage the form payment into
	 * the batch if the batch is empty (addPayment validates the form and bails
	 * loudly if something's missing), then run the one existing post path.
	 */
	postFromRemit(): void {
		if (!this.payments.length) {
			this.addPayment();
			if (!this.payments.length) { return; }   // form invalid — errors already shown
		}
		this.postPayments();
	}

	allocationFrom(event: boolean, transaction: any) {
		// The row object is the only durable record of the tick — the checkbox
		// element under the viewport gets handed to a different row on scroll.
		transaction.selected = event;
		if (!event) {
			const index = this.allocations_from.indexOf(transaction);
			if (index >= 0) {
				this.allocations_from.splice(index, 1);
				transaction.allocation = 0;
			}
		} else {
			if (this.allocations_from.indexOf(transaction) < 0) {
				this.allocations_from.push(transaction);
				transaction.allocation = transaction.leftto;
			}
		}
		this.updateAllocationTotals();
	}

	allocationTo(event: boolean, transaction: any) {
		transaction.selected = event;
		if (!event) {
			const index = this.allocations_to.indexOf(transaction);
			if (index >= 0) {
				this.allocations_to.splice(index, 1);
				transaction.allocation = 0;
			}
		} else {
			if (this.allocations_to.indexOf(transaction) < 0) {
				this.allocations_to.push(transaction);
				transaction.allocation = transaction.leftto;
			}
		}
		this.updateAllocationTotals();
	}

	updateAllocationFrom(value: string, transaction: any) {
		const index = this.allocations_from.indexOf(transaction);
		if (index >= 0) {
			const amount = parseFloat(value) || 0;
			if (amount > parseFloat(transaction.leftto)) {
				transaction.allocation = parseFloat(transaction.leftto);
			} else {
				transaction.allocation = amount;
			}
		}
		this.updateAllocationTotals();
	}

	updateAllocationTo(value: string, transaction: any) {
		const index = this.allocations_to.indexOf(transaction);
		if (index >= 0) {
			const amount = parseFloat(value) || 0;
			if (amount > parseFloat(transaction.leftto)) {
				transaction.allocation = parseFloat(transaction.leftto);
			} else {
				transaction.allocation = amount;
			}
		}
		this.updateAllocationTotals();
	}

	updateAllocationTotals() {
		this.allocation_from_total = this.allocations_from.reduce(function(accumulator: number, transaction: any) {
			return accumulator + parseFloat(transaction.allocation || 0);
		}, 0);

		this.allocation_to_total = this.allocations_to.reduce(function(accumulator: number, transaction: any) {
			return accumulator + parseFloat(transaction.allocation || 0);
		}, 0);
	}

	allocatePayments() {
		if (this.allocations_from.length === 0 || this.allocations_to.length === 0) {
			this.globalSearchService.showNotification('Please select payments/credits and invoices to allocate', 'warning', 'bottom', 'right');
			return;
		}

		const from_transactions = this.allocations_from.map((t: any) => {
			return {
				id: t.id,
				supplierid: t.supplierno || this.vendor.supplierid,
				allocation: t.allocation || 0
			};
		});

		const to_transactions = this.allocations_to.map((t: any) => {
			return {
				id: t.id,
				supplierid: t.supplierno || this.vendor.supplierid,
				allocation: t.allocation || 0
			};
		});

		const userid = this.user && this.user.user ? this.user.user.userid : '';

		const data = {
			from: from_transactions,
			to: to_transactions,
			vendor: this.vendor,
			userid: userid
		};

		this.paymentsService.addVendorPaymentAllocations(data).subscribe((result: any) => {
			if (result.success) {
				this.globalSearchService.showNotification('Allocations processed successfully', 'success', 'bottom', 'right');
				this.allocations_from = [];
				this.allocations_to = [];
				this.allocation_from_total = 0;
				this.allocation_to_total = 0;
				this.loadVendorTransactions();
				this.newvendordata.emit(this.vendor.supplierid);
			} else {
				this.globalSearchService.showNotification('Error processing allocations', 'error', 'bottom', 'right');
			}
		}, (error: any) => {
			this.globalSearchService.showNotification('Error processing allocations', 'error', 'bottom', 'right');
		});
	}

}
